import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, query, where, limit, orderBy } from 'firebase/firestore';
import { ref, getDownloadURL, listAll } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { Event } from '../../types/Event';

export const useEventBackgrounds = (events: Event[]) => {
  const [eventBackgrounds, setEventBackgrounds] = useState<Record<string, string>>(() => {
    const savedBackgrounds = localStorage.getItem('eventBackgrounds_v2');
    return savedBackgrounds ? JSON.parse(savedBackgrounds) : {};
  });

  const fetchedRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (Object.keys(eventBackgrounds).length > 0) {
      localStorage.setItem('eventBackgrounds_v2', JSON.stringify(eventBackgrounds));
    }
  }, [eventBackgrounds]);

  const fetchImageFromStorage = async (storagePath: string, eventId: string) => {
    try {
      if (eventBackgrounds[eventId]) return;
      
      const storageRef = ref(storage, storagePath);
      const url = await getDownloadURL(storageRef);
      
      const img = new Image();
      img.src = url;
      img.onload = () => {
        setEventBackgrounds(prev => ({ ...prev, [eventId]: url }));
      };
      img.onerror = () => {
        setEventBackgrounds(prev => ({ ...prev, [eventId]: '/images/event-default-bg.jpg' }));
      };
    } catch (error) {
      setEventBackgrounds(prev => ({ ...prev, [eventId]: '/images/event-default-bg.jpg' }));
    }
  };

  // Fonction de secours pour chercher des photos directement dans Storage (similaire à EventDebrief)
  const fetchRandomPhotoFromStorage = async (startDate: Date, endDate: Date): Promise<string | null> => {
    try {
      // Déterminer les dossiers mensuels à explorer
      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth() + 1;
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth() + 1;
      
      const foldersToCheck: string[] = [];
      
      // Si même année et même mois
      if (startYear === endYear && startMonth === endMonth) {
        foldersToCheck.push(`photos/${startYear}/${startMonth.toString().padStart(2, '0')}`);
      } else {
        // Sinon, parcourir tous les mois concernés
        let currentYear = startYear;
        let currentMonth = startMonth;
        
        while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
          foldersToCheck.push(`photos/${currentYear}/${currentMonth.toString().padStart(2, '0')}`);
          
          currentMonth++;
          if (currentMonth > 12) {
            currentMonth = 1;
            currentYear++;
          }
        }
      }
      
      // Récupérer toutes les photos des dossiers concernés
      const allPhotos: string[] = [];
      
      for (const folder of foldersToCheck) {
        try {
          const folderRef = ref(storage, folder);
          const filesList = await listAll(folderRef);
          
          // Pour chaque fichier, vérifier s'il est dans la plage de dates
          for (const fileRef of filesList.items) {
            // Essayer d'extraire la date du nom de fichier (format: YYYYMMDD_HHMMSS)
            const dateMatch = fileRef.name.match(/(\d{8})_(\d{6})/);
            
            if (dateMatch) {
              const dateStr = dateMatch[1];
              const timeStr = dateMatch[2];
              const year = parseInt(dateStr.substring(0, 4));
              const month = parseInt(dateStr.substring(4, 6)) - 1;
              const day = parseInt(dateStr.substring(6, 8));
              const hour = parseInt(timeStr.substring(0, 2));
              const minute = parseInt(timeStr.substring(2, 4));
              const second = parseInt(timeStr.substring(4, 6));
              
              const photoDate = new Date(year, month, day, hour, minute, second);
              
              if (photoDate >= startDate && photoDate <= endDate) {
                // Si la date est bonne, on ajoute le chemin (on récupérera l'URL plus tard si choisi)
                allPhotos.push(fileRef.fullPath);
              }
            } else {
              // Si pas de date dans le nom, on suppose que c'est bon si c'est dans le bon dossier
              // C'est une approximation pour les cas désespérés
              allPhotos.push(fileRef.fullPath);
            }
          }
        } catch (err) {
          console.error(`Erreur lors de la liste des fichiers dans ${folder}:`, err);
        }
      }
      
      if (allPhotos.length > 0) {
        const randomPath = allPhotos[Math.floor(Math.random() * allPhotos.length)];
        const storageRef = ref(storage, randomPath);
        return await getDownloadURL(storageRef);
      }
      
      return null;
    } catch (err) {
      console.error('Erreur lors de la récupération des photos depuis Storage:', err);
      return null;
    }
  };

  const fetchEventBackgroundImage = useCallback(async (event: Event) => {
    if (!event.id || !event.startDate || !event.endDate) return;

    try {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      
      const startDate = new Date(eventStart);
      startDate.setDate(startDate.getDate() - 2);
      
      const endDate = new Date(eventEnd);
      endDate.setDate(endDate.getDate() + 2);
      
      const photosRef = collection(db, 'photos');
      const q = query(
        photosRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc'),
        limit(10)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        // Premier fallback : recherche élargie dans Firestore (mois entier)
        const eventYear = eventStart.getFullYear();
        const eventMonth = eventStart.getMonth();
        const monthStart = new Date(eventYear, eventMonth, 1);
        const monthEnd = new Date(eventYear, eventMonth + 1, 0);
        
        const monthQuery = query(
          photosRef,
          where('date', '>=', monthStart),
          where('date', '<=', monthEnd),
          limit(20)
        );
        
        const monthSnapshot = await getDocs(monthQuery);
        
        if (!monthSnapshot.empty) {
          const randomIndex = Math.floor(Math.random() * monthSnapshot.size);
          const randomDoc = monthSnapshot.docs[randomIndex];
          const photoData = randomDoc.data();
          
          if (photoData.storagePath) {
            const storageRef = ref(storage, photoData.storagePath);
            const url = await getDownloadURL(storageRef);
            setEventBackgrounds(prev => ({ ...prev, [event.id as string]: url }));
          } else if (photoData.url) {
            setEventBackgrounds(prev => ({ ...prev, [event.id as string]: photoData.url }));
          } else {
            // Si pas de résultat utilisable dans Firestore, essayer Storage direct
            const storageUrl = await fetchRandomPhotoFromStorage(startDate, endDate);
            if (storageUrl) {
              setEventBackgrounds(prev => ({ ...prev, [event.id as string]: storageUrl }));
            } else {
              setEventBackgrounds(prev => ({ ...prev, [event.id as string]: '/images/event-default-bg.jpg' }));
            }
          }
        } else {
          // Second fallback : recherche par chemin dans Firestore (comme EventDebrief)
          // Utile si les photos sont dans des sous-dossiers ou n'ont pas de champ date valide
          const monthStr = (eventMonth + 1).toString().padStart(2, '0');
          const pathPrefix = `photos/${eventYear}/${monthStr}/`;
          
          const pathQuery = query(
            photosRef,
            where('path', '>=', pathPrefix),
            where('path', '<', pathPrefix + '\uf8ff'),
            limit(20)
          );
          
          const pathSnapshot = await getDocs(pathQuery);
          
          if (!pathSnapshot.empty) {
            const randomIndex = Math.floor(Math.random() * pathSnapshot.size);
            const randomDoc = pathSnapshot.docs[randomIndex];
            const photoData = randomDoc.data();
            
            if (photoData.storagePath || photoData.path) {
              const storagePath = photoData.storagePath || photoData.path;
              try {
                const storageRef = ref(storage, storagePath);
                const url = await getDownloadURL(storageRef);
                setEventBackgrounds(prev => ({ ...prev, [event.id as string]: url }));
              } catch (err) {
                // Si erreur (ex: fichier déplacé), on tente le storage direct
                const storageUrl = await fetchRandomPhotoFromStorage(startDate, endDate);
                if (storageUrl) {
                  setEventBackgrounds(prev => ({ ...prev, [event.id as string]: storageUrl }));
                } else {
                  setEventBackgrounds(prev => ({ ...prev, [event.id as string]: '/images/event-default-bg.jpg' }));
                }
              }
            } else {
              // Si pas de path, storage direct
              const storageUrl = await fetchRandomPhotoFromStorage(startDate, endDate);
              if (storageUrl) {
                setEventBackgrounds(prev => ({ ...prev, [event.id as string]: storageUrl }));
              } else {
                setEventBackgrounds(prev => ({ ...prev, [event.id as string]: '/images/event-default-bg.jpg' }));
              }
            }
          } else {
            // Troisième fallback : recherche directe dans Storage
            const storageUrl = await fetchRandomPhotoFromStorage(startDate, endDate);
            if (storageUrl) {
              setEventBackgrounds(prev => ({ ...prev, [event.id as string]: storageUrl }));
            } else {
              setEventBackgrounds(prev => ({ ...prev, [event.id as string]: '/images/event-default-bg.jpg' }));
            }
          }
        }
      } else {
        const randomIndex = Math.floor(Math.random() * querySnapshot.size);
        const randomDoc = querySnapshot.docs[randomIndex];
        const photoData = randomDoc.data();
        
        if (photoData.url) {
          const img = new Image();
          img.src = photoData.url;
          img.onload = () => {
            setEventBackgrounds(prev => ({ ...prev, [event.id as string]: photoData.url }));
          };
          img.onerror = () => {
            if (photoData.storagePath) {
              fetchImageFromStorage(photoData.storagePath, event.id as string);
            } else {
              setEventBackgrounds(prev => ({ ...prev, [event.id as string]: '/images/event-default-bg.jpg' }));
            }
          };
        } else if (photoData.storagePath) {
          fetchImageFromStorage(photoData.storagePath, event.id as string);
        } else {
          setEventBackgrounds(prev => ({ ...prev, [event.id as string]: '/images/event-default-bg.jpg' }));
        }
      }
    } catch (err) {
      setEventBackgrounds(prev => ({ ...prev, [event.id as string]: '/images/event-default-bg.jpg' }));
    }
  }, []);

  useEffect(() => {
    const now = new Date();
    const pastEvents = events.filter(event => {
      const endDate = new Date(event.endDate);
      return endDate <= now;
    });
    
    pastEvents.forEach(event => {
      if (event.id && !eventBackgrounds[event.id] && !fetchedRef.current[event.id]) {
        fetchedRef.current[event.id] = true;
        fetchEventBackgroundImage(event);
      }
    });
  }, [events, eventBackgrounds, fetchEventBackgroundImage]);

  return eventBackgrounds;
};
